import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDownRegular, ChevronUpRegular, DismissRegular } from "@fluentui/react-icons";

import styles from "./DocumentDropdown.module.css";

// Define props type
interface DocumentDropdownProps {
  documents: string[];
  documentOrder: string[];
  selectedDocuments: string[];
  setSelectedDocuments: (documents: string[]) => void;
  documentNameMapping: Record<string, string>;
  triggerLabel?: string;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const DocumentDropdown: React.FC<DocumentDropdownProps> = ({
  documents,
  documentOrder,
  selectedDocuments,
  setSelectedDocuments,
  documentNameMapping,
  triggerLabel = "Select standards",
  minimized,
  onMinimizedChange,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [internalMinimized, setInternalMinimized] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeFacet, setActiveFacet] = useState<string>("All");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const openButtonRef = useRef<HTMLButtonElement | null>(null);

  const isMinimized = minimized ?? internalMinimized;
  const setMinimized = (next: boolean) => {
    if (onMinimizedChange) onMinimizedChange(next);
    else setInternalMinimized(next);
  };

  const orderedDocuments = useMemo(() => {
    const available = Array.from(new Set((documents ?? []).map(d => d.trim())));
    const order = Array.from(new Set((documentOrder ?? []).map(d => d.trim())));

    if (available.length === 0) return order;

    const availableSet = new Set(available);
    const inOrder = order.filter(d => availableSet.has(d));
    const notInOrder = available.filter(d => !order.includes(d));
    return [...inOrder, ...notInOrder];
  }, [documents, documentOrder]);

  const getDisplayName = (doc: string) => documentNameMapping[doc] || doc;

  const getCategory = (doc: string): string => {
    const title = getDisplayName(doc).toLowerCase();

    if (/\bcpc\b/.test(title)) return "CPC";
    if (/\bisic\b/.test(title) && (title.includes("rev.5") || title.includes("rev 5") || title.includes("rev5"))) return "ISIC5";
    if (/\bseea\b/.test(title)) return "SEEA";

    // Override: keep HSS in "Other" even though it contains "securities"
    if (title.includes("handbook on securities statistics")) return "Other";

    if (title.includes("system of national accounts") || title.includes("national accounts")) return "National Accounts";
    //if (title.includes("balance of payments") || title.includes("bpm")) return "Balance of Payments";
    if (title.includes("balance of payments") || title.includes("bpm") || title.includes("external debt statistics")) return "Balance of Payments";
    if (title.includes("price index") || title.includes("cpi") || title.includes("ppi")) return "Prices";
    if (title.includes("financial soundness") || title.includes("fsi")) return "Financial Soundness";
    if (title.includes("government financial statistics") || title.includes("government finance statistics") || /\bgfs\b/.test(title)) return "GFS Manuals";
    if (title.includes("monetary") || title.includes("securities")) return "Monetary & Financial";
    if (title.includes("environmental") || title.includes("economic accounting") || title.includes("seea")) return "Environment";

    return "Other";
  };

  const facets = useMemo(() => {
    const facetCounts = new Map<string, number>();
    orderedDocuments.forEach(doc => {
      const cat = getCategory(doc);
      facetCounts.set(cat, (facetCounts.get(cat) ?? 0) + 1);
    });

    const sorted = Array.from(facetCounts.keys()).sort((a, b) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });
    return ["All", ...sorted];
  }, [orderedDocuments]);

  const facetCounts = useMemo(() => {
    const counts = new Map<string, number>();
    orderedDocuments.forEach(doc => {
      const cat = getCategory(doc);
      counts.set(cat, (counts.get(cat) ?? 0) + 1);
    });
    return counts;
  }, [orderedDocuments]);

  const filteredDocuments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return orderedDocuments.filter(doc => {
      if (activeFacet !== "All" && getCategory(doc) !== activeFacet) return false;
      if (!q) return true;

      const title = getDisplayName(doc).toLowerCase();
      return title.includes(q);
    });
  }, [orderedDocuments, activeFacet, searchQuery]);

  const categoryDocuments = useMemo(() => {
    if (activeFacet === "All") return orderedDocuments;
    return orderedDocuments.filter(doc => getCategory(doc) === activeFacet);
  }, [orderedDocuments, activeFacet]);

  const bulkSelectionState = useMemo(() => {
    const selectedSet = new Set(selectedDocuments);
    const total = categoryDocuments.length;
    const selectedCount = categoryDocuments.reduce((acc, doc) => acc + (selectedSet.has(doc) ? 1 : 0), 0);
    const allSelected = total > 0 && selectedCount === total;
    return { total, selectedCount, allSelected };
  }, [categoryDocuments, selectedDocuments]);

  // Close on Escape
  useEffect(() => {
    if (!isDrawerOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsDrawerOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isDrawerOpen]);

  // Focus search on open; restore focus to trigger on close
  useEffect(() => {
    if (isDrawerOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
      return;
    }

    requestAnimationFrame(() => openButtonRef.current?.focus());
  }, [isDrawerOpen]);

  // Prevent background scroll while the drawer is open (especially important on mobile)
  useEffect(() => {
    if (!isDrawerOpen) return;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isDrawerOpen]);

  // If the user minimizes the selector, ensure the modal is closed.
  useEffect(() => {
    if (isMinimized && isDrawerOpen) setIsDrawerOpen(false);
  }, [isMinimized, isDrawerOpen]);

  // Handle checkbox selection
  const handleCheckboxChange = (doc: string) => {
    const updatedSelection = selectedDocuments.includes(doc)
      ? selectedDocuments.filter((d) => d !== doc)
      : [...selectedDocuments, doc];

    setSelectedDocuments(updatedSelection);
  };

  const handleToggleSelectAllInCategory = () => {
    if (categoryDocuments.length === 0) return;

    if (bulkSelectionState.allSelected) {
      const categorySet = new Set(categoryDocuments);
      setSelectedDocuments(selectedDocuments.filter(d => !categorySet.has(d)));
      return;
    }

    const merged = new Set(selectedDocuments);
    categoryDocuments.forEach(d => merged.add(d));
    setSelectedDocuments(Array.from(merged));
  };

  const selectedPreview = useMemo(() => {
    if (selectedDocuments.length === 0) return "No documents selected";
    const names = selectedDocuments.map(getDisplayName);
    const firstTwo = names.slice(0, 2).join(", ");
    const extra = names.length > 2 ? ` +${names.length - 2}` : "";
    return `${firstTwo}${extra}`;
  }, [selectedDocuments, documentNameMapping]);

  const getYear = (doc: string) => {
    const title = getDisplayName(doc);
    const match = title.match(/\b(19|20)\d{2}\b/);
    return match?.[0];
  };

  const labelBPM = (lower: string, year?: string) => {
    if (!(lower.includes("balance of payments") || lower.includes("bpm"))) return;

    // Explicitly distinguish BPM7/BPM6/BPM5
    if (/(\bbpm\s*7\b|seventh\s+edition|seventh)/.test(lower)) return "BPM7";
    if (/(\bbpm\s*6\b|sixth\s+edition|sixth)/.test(lower)) return "BPM6";
    if (/(\bbpm\s*5\b|fifth\s+edition|fifth)/.test(lower)) return "BPM5";
    if (year) return `BPM${year}`;
    return "BPM";
  };

  const labelSNA = (lower: string, year?: string) => {
    if (!(lower.includes("system of national accounts") || /\bsna\b/.test(lower))) return;
    return year ? `SNA${year}` : "SNA";
  };

  const labelSEEA = (lower: string, year?: string) => {
    if (!(lower.includes("environmental") || lower.includes("economic accounting") || /\bseea\b/.test(lower))) return;
    return year ? `SEEA${year}` : "SEEA";
  };

  const labelGFS = (lower: string, year?: string) => {
    if (!(lower.includes("government finance") || /\bgfs\b/.test(lower))) return;
    return year ? `GFS${year}` : "GFS";
  };

  const labelFSI = (lower: string, year?: string) => {
    if (!(lower.includes("financial soundness") || /\bfsi\b/.test(lower))) return;
    return year ? `FSI${year}` : "FSI";
  };

  const labelMFS = (lower: string, year?: string) => {
    if (!(lower.includes("monetary and financial") || /\bmfs\b/.test(lower))) return;
    return year ? `MFS${year}` : "MFS";
  };

  const labelHSS = (lower: string) => {
    if (!lower.includes("securities statistics")) return;
    return "HSS";
  };

  const labelCPI = (lower: string, year?: string) => {
    if (!(lower.includes("consumer price") || /\bcpi\b/.test(lower))) return;
    return year ? `CPI${year}` : "CPI";
  };

  const labelPPI = (lower: string, year?: string) => {
    if (!(lower.includes("producer price") || /\bppi\b/.test(lower))) return;
    return year ? `PPI${year}` : "PPI";
  };

  const labelQNA = (lower: string, year?: string) => {
    if (!(lower.includes("quarterly national accounts") || /\bqna\b/.test(lower))) return;
    return year ? `QNA${year}` : "QNA";
  };

  const labelESA = (lower: string, year?: string) => {
    if (!(lower.includes("european system of accounts") || /\besa\b/.test(lower))) return;
    return year ? `ESA${year}` : "ESA";
  };

  const fallbackInitialsLabel = (title: string, year?: string) => {
    // Fallback: stable initials (+ year)
    const stop = new Set([
      "the",
      "of",
      "and",
      "on",
      "for",
      "a",
      "an",
      "manual",
      "guide",
      "handbook",
      "statistics",
      "statistical",
      "compilation",
      "international",
      "edition",
      "positions",
    ]);

    const words = title
      .replace(/[()–—-]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .filter(w => !stop.has(w.toLowerCase()));

    const initials = words
      .slice(0, 4)
      .map(w => w[0]?.toUpperCase())
      .join("");

    if (!initials) return year ? `DOC${year}` : "DOC";
    return year ? `${initials}${year}` : initials;
  };

  const getChipLabel = (doc: string) => {
    const chipLabelOverrides: Record<string, string> = {
      "CPC_Ver_3_Intro_2024.pdf": "CPC2024",
      "CPC_Ver_Explanatory_Notes_2025.pdf": "CPCEN2025",
      "ISIC5_Intro_2024.pdf": "ISIC5",
      "ISIC5_Explantory_Notes_2024.pdf": "ISIC5EN",
      "SEEA_EA_2024.pdf": "SEEAEA2024",
      "SEEA AFF_2020.pdf": "SEEAAFF2020",
      "SEEA-Energy_2019.pdf": "SEEAEN2020",
      "SEEA-Water_2012.pdf": "SEEAW2012",
    };

    const override = chipLabelOverrides[doc];
    if (override) return override;

    const title = getDisplayName(doc);
    const lower = title.toLowerCase();
    const year = getYear(doc);

    return (
      labelBPM(lower, year) ||
      labelSNA(lower, year) ||
      labelSEEA(lower, year) ||
      labelGFS(lower, year) ||
      labelFSI(lower, year) ||
      labelMFS(lower, year) ||
      labelHSS(lower) ||
      labelCPI(lower, year) ||
      labelPPI(lower, year) ||
      labelQNA(lower, year) ||
      labelESA(lower, year) ||
      fallbackInitialsLabel(title, year)
    );
  };

  const handleRemoveSelected = (doc: string) => {
    setSelectedDocuments(selectedDocuments.filter(d => d !== doc));
  };

  return (
    <div className={styles.wrapper}>
      {isMinimized ? (
        <div className="selector-minimized-bar">
          <button
            type="button"
            className="btn-select-documents btn-select-documents--compact btn-select-documents--minimized"
            onClick={() => setMinimized(false)}
            aria-label="Show standards selector"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <span>Standards</span>
            {selectedDocuments.length > 0 && <span className="badge">{selectedDocuments.length}</span>}
            <ChevronDownRegular aria-hidden="true" />
          </button>
        </div>
      ) : (
        <section className="selector-trigger-section">
          <div className="selector-trigger-row">
            <div className="selector-trigger-left">
              <div className="selector-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="selector-text">
                <button
                  ref={openButtonRef}
                  className="btn-select-documents btn-select-documents--compact"
                  type="button"
                  onClick={() => setIsDrawerOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={isDrawerOpen}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  {triggerLabel}
                  {selectedDocuments.length > 0 && <span className="badge">{selectedDocuments.length}</span>}
                </button>
                <p>{selectedPreview}</p>
              </div>
            </div>

            <div className="selector-trigger-actions">
              <button
                type="button"
                className="selector-collapse-btn"
                onClick={() => {
                  setIsDrawerOpen(false);
                  setMinimized(true);
                }}
                aria-label="Hide standards selector"
              >
                <ChevronUpRegular aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="selected-chips-main" aria-label="Selected documents">
            {selectedDocuments.map((doc) => (
              <span key={doc} className="selected-chip">
                {getChipLabel(doc)}
                <button
                  type="button"
                  className="chip-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveSelected(doc);
                  }}
                  aria-label={`Remove ${getDisplayName(doc)}`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </section>
      )}

      <div
        className={`modal-backdrop${isDrawerOpen ? " visible" : ""}`}
        id="modal-backdrop"
        onClick={() => setIsDrawerOpen(false)}
        aria-hidden={!isDrawerOpen}
      />

      <div
        className={`modal${isDrawerOpen ? " visible" : ""}`}
        id="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-hidden={!isDrawerOpen}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-title">
            <div className="modal-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div>
              <h2 id="modal-title">Select Documents</h2>
              <p>Choose one or more manuals to reference</p>
            </div>
          </div>
          <button className="modal-close" type="button" onClick={() => setIsDrawerOpen(false)} aria-label="Close modal">
            <DismissRegular />
          </button>
        </div>

        <div className="modal-body">
          <div className="search-container">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Search documents..."
              aria-label="Search documents"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="category-pills" role="tablist" aria-label="Document categories">
            {facets.map((facet) => {
              const count = facet === "All" ? orderedDocuments.length : (facetCounts.get(facet) ?? 0);
              return (
                <button
                  key={facet}
                  type="button"
                  role="tab"
                  aria-selected={facet === activeFacet}
                  className={`category-pill${facet === activeFacet ? " active" : ""}`}
                  onClick={() => setActiveFacet(facet)}
                >
                  {facet}
                  <span className="count">{count}</span>
                </button>
              );
            })}
          </div>

          <div className="document-list" role="listbox" aria-multiselectable="true">
            {filteredDocuments.length > 0 ? (
              filteredDocuments.map((doc) => {
                const display = getDisplayName(doc);
                const isSelected = selectedDocuments.includes(doc);

                return (
                  <div
                    key={doc}
                    className={`document-card${isSelected ? " selected" : ""}`}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onClick={() => handleCheckboxChange(doc)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCheckboxChange(doc);
                      }
                    }}
                  >
                    <div className="document-checkbox" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div className="document-info">
                      <div className="document-title">{display}</div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state" role="status">
                <p>No results. Try a different search.</p>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="modal-footer-left">
            <span className="selection-summary">
              <strong id="selected-count">{selectedDocuments.length}</strong> of {orderedDocuments.length} selected
            </span>
            <button className="btn-clear-all" type="button" onClick={() => setSelectedDocuments([])}>
              Clear all
            </button>
          </div>
          <button className="btn-done" type="button" onClick={() => setIsDrawerOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentDropdown;
